import { FC, useState, useMemo, useCallback, ReactNode } from 'react'
import * as styles from 'components/features/socialFeed/FeedPost/FeedPostText.styl'
import * as cn from 'classnames'
import { ComposeType, Mention } from 'interfaces/feed'
import { parseText } from 'helpers/composeHelpers'
import some from 'lodash/some'
import { TagHighlight, LinkHighlight, MentionHighlight, ReadMoreHighlight } from 'components/features/socialFeed/FeedPost/FeedPostTextHighlight'

const MAX_TEXT_LENGTH = 200

export type FeedPostTextProps = {
  text: string
  className?: string
  mode: ComposeType
  mentions: Mention[]
}

export const FeedPostText: FC<FeedPostTextProps> = (props) => {
  const { className, mode, text: originText, mentions } = props

  const [isExpanded, setIsExpanded] = useState(false)

  const isTextTooLong = useMemo(() => {
    return originText.length > MAX_TEXT_LENGTH
  }, [originText])

  const text = useMemo(() => {
    if (isExpanded || !isTextTooLong) {
      return originText
    }

    let spaceIndex = originText.lastIndexOf(' ', MAX_TEXT_LENGTH)
    if (spaceIndex === -1) {
      spaceIndex = MAX_TEXT_LENGTH
    }

    return originText.substr(0, spaceIndex)
  }, [originText, isExpanded, isTextTooLong])

  const handleReadMode = useCallback(() => {
    setIsExpanded(true)
  }, [setIsExpanded])

  const textChildren = useMemo(() => {
    const decorations = parseText(text, mode)

    let prevOffset = 0
    const nodes = decorations.reduce<ReactNode[]>((acc, { start, end, type }) => {
      acc.push(text.substring(prevOffset, start))
      prevOffset = end

      const textPart = text.substring(start, end)
      const isTag = type === 'tag'
      const isLink = type === 'link'
      const isMention = !isTag && !isLink && some(mentions, { username: textPart.substr(1) })
      const delta = end - start
      if (isTag && delta > 1) {
        acc.push(<TagHighlight tag={ textPart } key={ start } />)

      } else if (isLink) {
        acc.push(<LinkHighlight url={ textPart } key={ start } />)

      } else if (isMention) {
        acc.push(<MentionHighlight mention={ textPart } key={ start } />)

      } else {
        acc.push(textPart)
      }

      return acc
    }, [])

    if (prevOffset < text.length) {
      nodes.push(text.substr(prevOffset))
    }

    if (!isExpanded && isTextTooLong) {
      nodes.push(<ReadMoreHighlight onClick={ handleReadMode } key='readMore' />)
    }

    return nodes
  }, [handleReadMode, isExpanded, isTextTooLong, mentions, mode, text])

  return (
    <div className={ cn(styles.container, className) }>
      { textChildren }
    </div>
  )
}
